/**
 * Fachliche Datenzugriffe. Die Views reden nur mit dieser Datei,
 * nie direkt mit IndexedDB.
 */
import type {
  Board,
  CanvasObject,
  DrawingDoc,
  Note,
  Project,
  ProjectVisibility,
  Release,
  ReleaseKind,
  Room,
  Setlist,
  Settings,
  Song,
  SystemRoomKey,
} from '../types.ts';
import { STORE, del, get, getAll, put } from './idb.ts';
import { currentUser } from '../features/auth/session.ts';

/** Zufaellige, kurze ID. crypto.randomUUID gibt es auf iPadOS 16+. */
export function newId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return 'id-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
}

/* ---------------------------------------------------------------- Songs */

export function emptySong(projectId: string, partial: Partial<Song> = {}): Song {
  const now = Date.now();
  return {
    id: newId(),
    projectId,
    title: '',
    artist: '',
    key: '',
    tempo: null,
    timeSignature: '',
    notes: '',
    body: '',
    scrollSpeed: null,
    createdAt: now,
    updatedAt: now,
    ...partial,
  };
}

export async function listSongs(projectId?: string): Promise<Song[]> {
  const all = await getAll<Song>(STORE.songs);
  return projectId === undefined ? all : all.filter((song) => song.projectId === projectId);
}

export async function getSong(id: string): Promise<Song | undefined> {
  return get<Song>(STORE.songs, id);
}

export async function saveSong(song: Song): Promise<Song> {
  const next = { ...song, updatedAt: Date.now() };
  await put(STORE.songs, next);
  return next;
}

/** Loescht den Song, seine Anmerkungen und entfernt ihn aus allen Setlists. */
export async function deleteSong(id: string): Promise<void> {
  await del(STORE.songs, id);
  await del(STORE.drawings, songSurfaceId(id));
  const setlists = await listSetlists();
  for (const sl of setlists) {
    if (sl.songIds.includes(id)) {
      await saveSetlist({ ...sl, songIds: sl.songIds.filter((s) => s !== id) });
    }
  }
  // Auch aus den Veroeffentlichungen, sonst zeigt ein Album ins Leere.
  const releases = await listReleases();
  for (const release of releases) {
    if (release.songIds.includes(id)) {
      await saveRelease({ ...release, songIds: release.songIds.filter((s) => s !== id) });
    }
  }
}

export type SortKey = 'title' | 'artist' | 'updatedAt' | 'key';

/** Filtert nach Suchbegriff und sortiert. Laeuft im Speicher – bei ein paar
 *  hundert Songs ist das deutlich schneller als der Umweg ueber Indizes. */
export function filterSongs(songs: Song[], query: string, sort: SortKey): Song[] {
  const q = query.trim().toLowerCase();
  const matched = q
    ? songs.filter((s) =>
        [s.title, s.artist, s.key, s.notes, s.body].join('\n').toLowerCase().includes(q),
      )
    : songs.slice();

  const byText = (a: string, b: string) => a.localeCompare(b, 'de', { sensitivity: 'base' });
  matched.sort((a, b) => {
    switch (sort) {
      case 'artist':
        return byText(a.artist, b.artist) || byText(a.title, b.title);
      case 'key':
        return byText(a.key, b.key) || byText(a.title, b.title);
      case 'updatedAt':
        return b.updatedAt - a.updatedAt;
      case 'title':
      default:
        return byText(a.title, b.title);
    }
  });
  return matched;
}

/* ------------------------------------------------------------- Setlists */

export function emptySetlist(projectId: string, partial: Partial<Setlist> = {}): Setlist {
  const now = Date.now();
  return {
    id: newId(),
    projectId,
    name: '',
    songIds: [],
    notes: '',
    createdAt: now,
    updatedAt: now,
    ...partial,
  };
}

export async function listSetlists(projectId?: string): Promise<Setlist[]> {
  const all = await getAll<Setlist>(STORE.setlists);
  const scoped = projectId === undefined ? all : all.filter((s) => s.projectId === projectId);
  scoped.sort((a, b) => b.updatedAt - a.updatedAt);
  return scoped;
}

export async function getSetlist(id: string): Promise<Setlist | undefined> {
  return get<Setlist>(STORE.setlists, id);
}

export async function saveSetlist(setlist: Setlist): Promise<Setlist> {
  const next = { ...setlist, updatedAt: Date.now() };
  await put(STORE.setlists, next);
  return next;
}

export async function deleteSetlist(id: string): Promise<void> {
  await del(STORE.setlists, id);
}

/* ------------------------------------------------------------- Projekte */

/**
 * Sichtbarkeit zur Auswahl. Die Liste ist die einzige Quelle fuer
 * Beschriftungen – die Oberflaeche erfindet keine eigenen.
 */
export const PROJECT_VISIBILITIES: {
  value: ProjectVisibility;
  label: string;
  hint: string;
}[] = [
  // Ehrlich: "Privat" ist heute eine Markierung, kein Zugriffsschutz in der
  // Cloud (docs/SECURITY.md, Befund S1). Der Text sagt es, bis die
  // Arbeitsbereiche mit echten Rechten kommen.
  { value: 'private', label: 'Privat', hint: 'Für dich markiert. Noch kein Zugriffsschutz: freigeschaltete Bandmitglieder könnten es in der Cloud technisch lesen.' },
  { value: 'public', label: 'Öffentlich', hint: 'Für die ganze Band gedacht und sichtbar.' },
];

/**
 * Die Sichtbarkeit eines Projekts – mit sicherem Standard.
 *
 * Alles, was den Status liest, geht hier durch. Dadurch steht die Regel
 * "im Zweifel privat" an genau einer Stelle: ein Projekt aus der Zeit vor
 * dem Feld, ein Projekt aus einer fremden Backup-Datei oder ein Datensatz
 * mit einem unbekannten Wert gilt als privat – nie versehentlich als
 * oeffentlich.
 */
export function projectVisibility(project: Pick<Project, 'visibility'>): ProjectVisibility {
  return project.visibility === 'public' ? 'public' : 'private';
}

export function visibilityLabel(visibility: ProjectVisibility): string {
  return PROJECT_VISIBILITIES.find((v) => v.value === visibility)?.label ?? 'Privat';
}

export function emptyProject(partial: Partial<Project> = {}): Project {
  const now = Date.now();
  return {
    id: newId(),
    name: '',
    subtitle: '',
    cover: 0,
    // Automatisch vergeben, noch nicht bewusst gewaehlt (siehe types.ts).
    coverChosen: false,
    // Ein neues Projekt startet privat. Wer teilen will, sagt es ausdruecklich.
    visibility: 'private',
    createdAt: now,
    updatedAt: now,
    ...partial,
  };
}

export async function listProjects(): Promise<Project[]> {
  const all = await getAll<Project>(STORE.projects);
  // Älteste zuerst, damit die Reihenfolge im Regal stabil bleibt.
  all.sort((a, b) => a.createdAt - b.createdAt);
  return all;
}

export async function getProject(id: string): Promise<Project | undefined> {
  return get<Project>(STORE.projects, id);
}

export async function saveProject(project: Project): Promise<Project> {
  const next = { ...project, updatedAt: Date.now() };
  await put(STORE.projects, next);
  return next;
}

/** Löscht ein Projekt mitsamt allem, was darin liegt. */
export async function deleteProject(id: string): Promise<void> {
  const [songs, setlists, notes, rooms, releases] = await Promise.all([
    listSongs(id),
    listSetlists(id),
    listNotes(id),
    listRooms(id),
    listReleases(id),
  ]);
  for (const song of songs) await deleteSong(song.id);
  for (const setlist of setlists) await deleteSetlist(setlist.id);
  for (const note of notes) await deleteNote(note.id);
  for (const release of releases) await deleteRelease(release.id);
  // Die Band-Bibel des Projekts geht mit, samt Notizen und Handschrift.
  for (const room of rooms) await deleteRoom(room.id);
  // Ein selbst gezeichneter Einband haengt ebenfalls am Projekt.
  await del(STORE.drawings, coverSurfaceId(id));
  // Der eigene Einband dieser Person ebenso (die anderer Mitglieder liegen
  // in deren Einstellungen und fallen nicht mehr ins Gewicht).
  await del(STORE.settings, `cover:${id}`);
  await del(STORE.drawings, await myCoverSurfaceId(id));
  await del(STORE.projects, id);
}

/**
 * Sorgt dafür, dass es mindestens ein Projekt gibt und dass jeder Song,
 * jede Setlist und jede Notiz einem zugeordnet ist.
 *
 * Das betrifft Daten aus der Zeit vor der Projekt-Ebene: sie wandern in ein
 * automatisch angelegtes Projekt, statt verloren zu gehen.
 */
export async function ensureProjects(): Promise<Project[]> {
  let projects = await listProjects();

  /*
   * Nachtrag fuer Projekte ohne Sichtbarkeit.
   *
   * Gelesen wird der Status ohnehin ueber projectVisibility() und waere auch
   * ohne diesen Schritt korrekt privat. Er wird trotzdem einmal wirklich
   * hineingeschrieben, damit der Status im Datensatz steht und nicht nur in
   * einer Annahme – spaeteres Teilen oder eine Synchronisierung soll sich
   * nicht auf den Standard einer aelteren App-Fassung verlassen muessen.
   */
  const withoutVisibility = projects.filter((project) => project.visibility === undefined);
  if (withoutVisibility.length > 0) {
    for (const project of withoutVisibility) {
      await put(STORE.projects, { ...project, visibility: 'private' as ProjectVisibility });
    }
    projects = await listProjects();
  }

  const [songs, setlists, notes] = await Promise.all([
    getAll<Song>(STORE.songs),
    getAll<Setlist>(STORE.setlists),
    getAll<Note>(STORE.notes),
  ]);
  const orphanSongs = songs.filter((s) => !s.projectId);
  const orphanSetlists = setlists.filter((s) => !s.projectId);
  const orphanNotes = notes.filter((n) => !n.projectId);
  const hasOrphans =
    orphanSongs.length > 0 || orphanSetlists.length > 0 || orphanNotes.length > 0;

  if (projects.length === 0 && !hasOrphans) return projects;

  if (projects.length === 0) {
    await saveProject(emptyProject({ name: 'Meine Musik', cover: 0 }));
    projects = await listProjects();
  }
  if (!hasOrphans) return projects;

  const target = projects[0] as Project;
  for (const song of orphanSongs) await put(STORE.songs, { ...song, projectId: target.id });
  for (const setlist of orphanSetlists) {
    await put(STORE.setlists, { ...setlist, projectId: target.id });
  }
  for (const note of orphanNotes) await put(STORE.notes, { ...note, projectId: target.id });

  return projects;
}

/* ---------------------------------------------- Veroeffentlichungen */

export const RELEASE_KINDS: { value: ReleaseKind; label: string; plural: string }[] = [
  { value: 'album', label: 'Album', plural: 'Alben' },
  { value: 'ep', label: 'EP', plural: 'EPs' },
  { value: 'single', label: 'Single', plural: 'Singles' },
];

export function releaseKindLabel(kind: ReleaseKind): string {
  return RELEASE_KINDS.find((k) => k.value === kind)?.label ?? 'Album';
}

export function emptyRelease(projectId: string, partial: Partial<Release> = {}): Release {
  const now = Date.now();
  return {
    id: newId(),
    projectId,
    title: '',
    kind: 'album',
    year: '',
    pad: 0,
    songIds: [],
    notes: '',
    order: now,
    createdAt: now,
    updatedAt: now,
    ...partial,
  };
}

export async function listReleases(projectId?: string): Promise<Release[]> {
  const all = await getAll<Release>(STORE.releases);
  const scoped = projectId === undefined ? all : all.filter((r) => r.projectId === projectId);
  scoped.sort((a, b) => a.order - b.order || a.createdAt - b.createdAt);
  return scoped;
}

export async function getRelease(id: string): Promise<Release | undefined> {
  return get<Release>(STORE.releases, id);
}

export async function saveRelease(release: Release): Promise<Release> {
  const next = { ...release, updatedAt: Date.now() };
  await put(STORE.releases, next);
  return next;
}

/**
 * Loescht die Veroeffentlichung, NICHT ihre Songs.
 * Die Songs gehoeren dem Projekt, nicht dem Album – sonst waere ein Versehen
 * beim Aufraeumen teuer.
 */
export async function deleteRelease(id: string): Promise<void> {
  await del(STORE.releases, id);
}

/** Songs, die noch auf keiner Veroeffentlichung liegen. */
export function unassignedSongs(songs: Song[], releases: Release[]): Song[] {
  const taken = new Set(releases.flatMap((r) => r.songIds));
  return songs.filter((song) => !taken.has(song.id));
}

/** Kurzer Untertitel: "Album · 2026 · 9 Songs". */
export function releaseSummary(release: Release): string {
  const parts: string[] = [releaseKindLabel(release.kind)];
  if (release.year) parts.push(release.year);
  const count = release.songIds.length;
  parts.push(`${count} ${count === 1 ? 'Song' : 'Songs'}`);
  return parts.join(' · ');
}

/* ---------------------------------------------------------- Band-Bibel */

/**
 * Die vier festen Kategorien der Band-Bibel.
 *
 * Sie sind das Grundgeruest jedes Projekts und lassen sich weder umbenennen
 * noch loeschen – sonst loeste man versehentlich die Ablage der ganzen Band
 * auf. Eigene Raeume legt man DARIN an.
 */
export const SYSTEM_ROOMS: { key: SystemRoomKey; name: string; symbol: string }[] = [
  { key: 'general', name: 'Allgemein', symbol: '●' },
  { key: 'gigs', name: 'Gigs', symbol: '◆' },
  { key: 'songs', name: 'Songs', symbol: '♪' },
  { key: 'other', name: 'Sonstiges', symbol: '✳' },
];

/** Feste Kategorie? Die darf nicht angetastet werden. */
export function isSystemRoom(room: Room): boolean {
  return room.systemKey !== undefined;
}

export function emptyRoom(projectId: string, partial: Partial<Room> = {}): Room {
  const now = Date.now();
  return {
    id: newId(),
    projectId,
    name: '',
    symbol: '◇',
    order: 0,
    parentId: null,
    seeded: false,
    createdAt: now,
    updatedAt: now,
    ...partial,
  };
}

/** Alle Raeume eines Projekts, in Reihenfolge. */
export async function listRooms(projectId: string): Promise<Room[]> {
  const all = await getAll<Room>(STORE.rooms);
  const scoped = all.filter((room) => room.projectId === projectId);
  scoped.sort((a, b) => a.order - b.order || a.createdAt - b.createdAt);
  return scoped;
}

/** Die vier festen Kategorien, in ihrer festen Reihenfolge. */
export function systemRooms(rooms: Room[]): Room[] {
  const order = SYSTEM_ROOMS.map((r) => r.key);
  return rooms
    .filter(isSystemRoom)
    .sort((a, b) => order.indexOf(a.systemKey as SystemRoomKey) - order.indexOf(b.systemKey as SystemRoomKey));
}

/** Die eigenen Raeume innerhalb einer Kategorie. */
export function childRooms(rooms: Room[], parentId: string): Room[] {
  return rooms.filter((room) => room.parentId === parentId && !isSystemRoom(room));
}

export async function getRoom(id: string): Promise<Room | undefined> {
  return get<Room>(STORE.rooms, id);
}

export async function saveRoom(room: Room): Promise<Room> {
  const next = { ...room, updatedAt: Date.now() };
  await put(STORE.rooms, next);
  return next;
}

/**
 * Loescht den Raum mitsamt seinen Notizen und Unterraeumen.
 * Feste Kategorien lehnt die Funktion ab – der Schutz gehoert hierher und
 * nicht nur in die Oberflaeche, sonst haengt er an einem ausgeblendeten Knopf.
 */
export async function deleteRoom(id: string): Promise<void> {
  const room = await getRoom(id);
  if (room && isSystemRoom(room)) {
    throw new Error('Feste Kategorien der Band-Bibel lassen sich nicht loeschen.');
  }
  await removeRoomTree(id);
}

/** Loescht einen Raum und alles darunter, ohne den Schutz zu pruefen. */
async function removeRoomTree(id: string): Promise<void> {
  const boards = await listBoards(id);
  for (const board of boards) await deleteBoard(board.id);

  const room = await getRoom(id);
  if (room) {
    const siblings = await listRooms(room.projectId);
    for (const child of siblings.filter((r) => r.parentId === id)) {
      await removeRoomTree(child.id);
    }
  }
  await del(STORE.rooms, id);
}

/**
 * Sorgt dafuer, dass die vier festen Kategorien da sind – und zwar genau
 * einmal.
 *
 * Das laeuft bei jedem Oeffnen der Band-Bibel und raeumt dabei auch aeltere
 * Daten auf:
 *   - Raeume aus der Zeit vor den Kategorien werden anhand ihres Namens
 *     wiedererkannt und zur Kategorie befoerdert, statt doppelt anzulegen.
 *   - Alles andere ohne Zuordnung wandert unter "Sonstiges", damit es nicht
 *     unerreichbar wird.
 */
export async function ensureRooms(projectId: string): Promise<Room[]> {
  const existing = await listRooms(projectId);
  const byKey = new Map<SystemRoomKey, Room>();
  for (const room of existing) {
    if (room.systemKey) byKey.set(room.systemKey, room);
  }

  for (let i = 0; i < SYSTEM_ROOMS.length; i += 1) {
    const def = SYSTEM_ROOMS[i] as { key: SystemRoomKey; name: string; symbol: string };
    if (byKey.has(def.key)) continue;

    // Aus der Zeit vor den Kategorien: gleichnamiger Raum wird befoerdert.
    const legacy = existing.find(
      (room) =>
        !room.systemKey &&
        room.seeded &&
        room.name.trim().toLowerCase() === def.name.toLowerCase(),
    );
    if (legacy) {
      const promoted = await saveRoom({
        ...legacy,
        systemKey: def.key,
        symbol: legacy.symbol || def.symbol,
        parentId: null,
        order: i,
      });
      byKey.set(def.key, promoted);
      continue;
    }

    const created = await saveRoom(
      emptyRoom(projectId, {
        name: def.name,
        symbol: def.symbol,
        order: i,
        systemKey: def.key,
        parentId: null,
        seeded: true,
      }),
    );
    byKey.set(def.key, created);
  }

  // Heimatlose Raeume einsortieren, statt sie verschwinden zu lassen.
  const other = byKey.get('other');
  if (other) {
    for (const room of existing) {
      if (room.systemKey) continue;
      const parentExists = room.parentId && existing.some((r) => r.id === room.parentId);
      if (!parentExists) await saveRoom({ ...room, parentId: other.id });
    }
  }

  return listRooms(projectId);
}

/** Die feste Kategorie mit diesem Schluessel. */
export async function systemRoom(projectId: string, key: SystemRoomKey): Promise<Room> {
  const rooms = await ensureRooms(projectId);
  const found = rooms.find((room) => room.systemKey === key);
  // ensureRooms legt sie an, also kann das nur bei einem Fehler passieren.
  if (!found) throw new Error(`Kategorie "${key}" fehlt.`);
  return found;
}

/** Breite der Flaeche in Weltpixeln. Fest, weil x als Anteil gespeichert wird. */
export const WORLD_WIDTH = 6000;
/** Starthoehe; sie waechst, sobald man sich dem unteren Rand naehert. */
export const WORLD_HEIGHT = 4000;

export function emptyBoard(projectId: string, roomId: string, partial: Partial<Board> = {}): Board {
  const now = Date.now();
  return {
    id: newId(),
    projectId,
    roomId,
    title: '',
    order: now,
    objects: [],
    edges: [],
    worldHeight: WORLD_HEIGHT,
    background: 'dots',
    // Startansicht: links oben in der Welt, 100 Prozent.
    view: { x: 0, y: 0, scale: 1 },
    rev: 0,
    createdAt: now,
    updatedAt: now,
    ...partial,
  };
}

export async function listBoards(roomId: string): Promise<Board[]> {
  const all = await getAll<Board>(STORE.boards);
  const scoped = all.filter((board) => board.roomId === roomId);
  scoped.sort((a, b) => b.updatedAt - a.updatedAt);
  return scoped;
}

/** Alle Notizen eines Projekts, quer ueber die Raeume (fuer Zaehler, Backup). */
export async function listProjectBoards(projectId: string): Promise<Board[]> {
  const all = await getAll<Board>(STORE.boards);
  return all.filter((board) => board.projectId === projectId);
}

export async function getBoard(id: string): Promise<Board | undefined> {
  return get<Board>(STORE.boards, id);
}

export async function saveBoard(board: Board): Promise<Board> {
  const next = { ...board, rev: board.rev + 1, updatedAt: Date.now() };
  await put(STORE.boards, next);
  return next;
}

export async function deleteBoard(id: string): Promise<void> {
  await del(STORE.boards, id);
  // Die Handschrift darauf gehoert dazu.
  await del(STORE.drawings, boardSurfaceId(id));
}

/** Schluessel der Zeichenflaeche eines selbst gezeichneten Einbands (gemeinsam). */
export function coverSurfaceId(projectId: string): string {
  return `cover:${projectId}`;
}

/* ------------------------------------------------ Persoenlicher Einband */

/**
 * Der Einband gehoert zum Design und ist deshalb je Person eigen – wie
 * Hintergrund und Farben. Er liegt bei den Einstellungen des Benutzers
 * (in der Cloud unter users/<uid>/settings, also fuer niemanden sonst
 * sichtbar) unter "cover:<projektId>". Wer noch keinen eigenen gewaehlt
 * hat, sieht den gemeinsamen Einband aus dem Projekt.
 */
export type CoverFields = Pick<
  Project,
  'cover' | 'coverMode' | 'coverArtwork' | 'coverImage' | 'coverArtworkVersion' | 'coverChosen'
>;

/** Privat: nur ich sehe mein Design. Oeffentlich: alle sehen es. */
export type DesignVisibility = 'private' | 'public';

export interface PersonalCover extends CoverFields {
  /** "cover:<projektId>" */
  id: string;
  /** Fehlt bei aelteren Eintraegen = privat. */
  visibility?: DesignVisibility;
}

/** Nur die Einband-Felder eines Projekts oder eigenen Einbands. */
function coverFieldsOf(src: CoverFields): CoverFields {
  return {
    cover: src.cover,
    coverMode: src.coverMode,
    coverArtwork: src.coverArtwork,
    coverImage: src.coverImage,
    coverArtworkVersion: src.coverArtworkVersion,
    coverChosen: src.coverChosen,
  };
}

function personalCoverId(projectId: string): string {
  return `cover:${projectId}`;
}

export async function getMyCover(projectId: string): Promise<PersonalCover | undefined> {
  return get<PersonalCover>(STORE.settings, personalCoverId(projectId));
}

/** Alle eigenen Einbaende, nach Projekt-Id – fuers Regal. */
export async function listMyCovers(): Promise<Map<string, PersonalCover>> {
  const all = await getAll<PersonalCover & { id: string }>(STORE.settings);
  const mine = new Map<string, PersonalCover>();
  for (const doc of all) {
    if (typeof doc.id === 'string' && doc.id.startsWith('cover:')) mine.set(doc.id.slice(6), doc);
  }
  return mine;
}

/** Das Projekt so, wie es diese Person sieht: mit ihrem Einband, falls vorhanden. */
export function withMyCover(project: Project, mine: PersonalCover | undefined): Project {
  if (!mine) return project;
  // Nur die Einband-Felder: "visibility" des Einbands ist nicht die des Projekts.
  return { ...project, ...coverFieldsOf(mine) };
}

/**
 * Speichert den eigenen Einband. Ist er oeffentlich, bekommt das Projekt
 * denselben Stand – so sehen die anderen jede Aenderung mit.
 */
export async function saveMyCover(projectId: string, fields: CoverFields): Promise<void> {
  const before = await getMyCover(projectId);
  const doc: PersonalCover = {
    id: personalCoverId(projectId),
    ...coverFieldsOf(fields),
    visibility: before?.visibility ?? 'private',
  };
  await put(STORE.settings, doc);
  if (doc.visibility === 'public') {
    const project = await getProject(projectId);
    if (project) await saveProject({ ...project, ...coverFieldsOf(doc) });
  }
}

/**
 * Eigenen Einband oeffentlich oder privat stellen.
 *
 * Oeffentlich: der bisherige gemeinsame Einband wird am Projekt gemerkt
 * (coverBeforePublish) und durch meinen ersetzt. Privat: der gemerkte
 * gemeinsame Einband kommt zurueck – die anderen sehen meinen nicht mehr.
 */
export async function setMyCoverVisibility(project: Project, fields: CoverFields, visibility: DesignVisibility): Promise<Project> {
  await put(STORE.settings, { id: personalCoverId(project.id), ...coverFieldsOf(fields), visibility } satisfies PersonalCover);
  const fresh = (await getProject(project.id)) ?? project;
  if (visibility === 'public') {
    const next: Project = {
      ...fresh,
      coverBeforePublish: fresh.coverBeforePublish ?? coverFieldsOf(fresh),
      ...coverFieldsOf(fields),
    };
    return saveProject(next);
  }
  const { coverBeforePublish, ...rest } = fresh;
  return saveProject({ ...rest, ...(coverBeforePublish ?? {}) } as Project);
}

/* ------------------------------------------ Persoenliches Release-Cover */

/**
 * Cover von Alben, EPs und Singles: oeffentlich (am Release, alle sehen es)
 * oder privat (bei meinen Einstellungen unter "artwork:<releaseId>", nur
 * ich sehe es). Ohne Eintrag gilt das Cover am Release als oeffentlich.
 */
export interface PersonalArtwork {
  /** "artwork:<releaseId>" */
  id: string;
  image?: string;
  visibility: DesignVisibility;
}

export async function getMyArtwork(releaseId: string): Promise<PersonalArtwork | undefined> {
  return get<PersonalArtwork>(STORE.settings, `artwork:${releaseId}`);
}

export async function listMyArtworks(): Promise<Map<string, PersonalArtwork>> {
  const all = await getAll<PersonalArtwork>(STORE.settings);
  const mine = new Map<string, PersonalArtwork>();
  for (const doc of all) {
    if (typeof doc.id === 'string' && doc.id.startsWith('artwork:')) mine.set(doc.id.slice(8), doc);
  }
  return mine;
}

/** Das Cover, das diese Person sieht. */
export function visibleArtwork(release: Release, mine: PersonalArtwork | undefined): string | undefined {
  return mine?.visibility === 'private' ? mine.image : release.artwork;
}

/** Neues Cover waehlen oder entfernen – je nach Sichtbarkeit bei mir oder am Release. */
export async function setReleaseArtwork(release: Release, image: string | undefined): Promise<Release> {
  const mine = await getMyArtwork(release.id);
  if (mine?.visibility === 'private') {
    await put(STORE.settings, { ...mine, image } satisfies PersonalArtwork);
    return release;
  }
  return saveRelease({ ...release, artwork: image });
}

/** Cover oeffentlich oder privat stellen (siehe setMyCoverVisibility). */
export async function setArtworkVisibility(release: Release, visibility: DesignVisibility): Promise<Release> {
  const mine = await getMyArtwork(release.id);
  const fresh = (await getRelease(release.id)) ?? release;
  if (visibility === 'private') {
    await put(STORE.settings, {
      id: `artwork:${release.id}`,
      image: mine?.image ?? fresh.artwork,
      visibility,
    } satisfies PersonalArtwork);
    const { artworkBeforePublish, ...rest } = fresh;
    return saveRelease({ ...rest, artwork: artworkBeforePublish } as Release);
  }
  await put(STORE.settings, { id: `artwork:${release.id}`, image: mine?.image ?? fresh.artwork, visibility } satisfies PersonalArtwork);
  if (mine?.visibility !== 'private') return fresh;
  return saveRelease({ ...fresh, artworkBeforePublish: fresh.artwork, artwork: mine.image });
}

/**
 * Zeichenflaeche des eigenen Einbands. Die Striche liegen im gemeinsamen
 * "drawings"-Store, deshalb steckt die Benutzer-Id im Schluessel.
 */
export async function myCoverSurfaceId(projectId: string): Promise<string> {
  const me = await currentUser();
  return `cover:${projectId}:${me?.id ?? 'local'}`;
}

/** Schluessel der Zeichenflaeche einer Bibel-Notiz. */
export function boardSurfaceId(boardId: string): string {
  return `board:${boardId}`;
}

/** Erste Zeile eines Objekts oder der Titel – fuer die Liste. */
export function boardTitle(board: Board): string {
  if (board.title.trim() !== '') return board.title.trim();
  const withText = board.objects.find((o) => o.text.trim() !== '');
  if (!withText) return '';
  return (withText.text.split('\n')[0] as string).trim();
}

/** Kurze Beschreibung des Inhalts, z. B. "6 Objekte · Handschrift". */
export function boardSummary(board: Board, hasInk: boolean): string {
  const parts: string[] = [];
  const count = board.objects.length;
  if (count > 0) parts.push(`${count} ${count === 1 ? 'Objekt' : 'Objekte'}`);
  if (board.edges.length > 0) parts.push('Mindmap');
  if (hasInk) parts.push('Handschrift');
  return parts.length > 0 ? parts.join(' · ') : 'Leer';
}

/** Startgroesse je Objektart, in Weltpixeln. */
const OBJECT_SIZE: Record<CanvasObject['type'], { w: number; h: number }> = {
  node: { w: 180, h: 72 },
  text: { w: 260, h: 60 },
  image: { w: 320, h: 240 },
  audio: { w: 340, h: 118 },
  shape: { w: 240, h: 160 },
  checklist: { w: 260, h: 160 },
  card: { w: 260, h: 160 },
};

/** Grundschriftgroesse je Objektart, in Weltpixeln. */
export const DEFAULT_FONT_SIZE = 15;

export function defaultFontSize(type: CanvasObject['type']): number {
  return type === 'node' ? 17 : DEFAULT_FONT_SIZE;
}

/** Neues Objekt mit sinnvollen Vorgaben je Art. */
export function emptyObject(partial: Partial<CanvasObject> = {}): CanvasObject {
  const type = partial.type ?? 'card';
  const size = OBJECT_SIZE[type] ?? OBJECT_SIZE.card;
  return {
    id: newId(),
    type,
    x: 0,
    y: 0,
    ...size,
    text: '',
    color: '',
    // Formen brauchen eine Vorgabe, sonst waeren sie unsichtbar.
    ...(type === 'shape' ? { shape: 'rect' as const } : {}),
    ...partial,
  };
}

/* -------------------------------------------------------------- Notizen */

export function emptyNote(projectId: string, partial: Partial<Note> = {}): Note {
  const now = Date.now();
  return { id: newId(), projectId, body: '', createdAt: now, updatedAt: now, ...partial };
}

export async function listNotes(projectId?: string): Promise<Note[]> {
  const all = await getAll<Note>(STORE.notes);
  const scoped = projectId === undefined ? all : all.filter((n) => n.projectId === projectId);
  // Zuletzt bearbeitet zuerst, wie man es von Notizen-Apps kennt.
  scoped.sort((a, b) => b.updatedAt - a.updatedAt);
  return scoped;
}

export async function getNote(id: string): Promise<Note | undefined> {
  return get<Note>(STORE.notes, id);
}

export async function saveNote(note: Note): Promise<Note> {
  const next = { ...note, updatedAt: Date.now() };
  await put(STORE.notes, next);
  return next;
}

export async function deleteNote(id: string): Promise<void> {
  await del(STORE.notes, id);
  // Zeichnungen auf dieser Notiz gehen mit.
  await del(STORE.drawings, screenSurfaceId(`/note/${id}`));
}

/** Erste nicht leere Zeile, sie dient als Titel. */
export function noteTitle(note: Note): string {
  const line = note.body.split('\n').find((l) => l.trim() !== '');
  return line ? line.trim() : '';
}

/** Der Text nach der Titelzeile, für die Vorschau in der Liste. */
export function notePreview(note: Note): string {
  const lines = note.body.split('\n');
  const first = lines.findIndex((l) => l.trim() !== '');
  if (first === -1) return '';
  return lines.slice(first + 1).join(' ').replace(/\s+/g, ' ').trim();
}

/** Filtert Notizen nach einem Suchbegriff. */
export function filterNotes(notes: Note[], query: string): Note[] {
  const q = query.trim().toLowerCase();
  if (q === '') return notes.slice();
  return notes.filter((note) => note.body.toLowerCase().includes(q));
}

/* ---------------------------------------------------------- Zeichnungen */

/** Schluessel der Zeichenflaeche eines Songs. */
export function songSurfaceId(songId: string): string {
  return `song:${songId}`;
}

/** Schluessel der Zeichenflaeche eines Bildschirms. */
export function screenSurfaceId(name: string): string {
  return `screen:${name}`;
}

export async function getDrawing(id: string): Promise<DrawingDoc> {
  const doc = await get<DrawingDoc>(STORE.drawings, id);
  return doc ?? { id, strokes: [], updatedAt: 0 };
}

export async function saveDrawing(doc: DrawingDoc): Promise<void> {
  // Leere Flaechen nicht speichern – sonst fuellt sich die Datenbank mit
  // leeren Datensaetzen fuer jeden besuchten Bildschirm.
  if (doc.strokes.length === 0) {
    await del(STORE.drawings, doc.id);
    return;
  }
  await put(STORE.drawings, { ...doc, updatedAt: Date.now() });
}

/* -------------------------------------------------------- Einstellungen */

export const DEFAULT_SETTINGS: Settings = {
  id: 'app',
  activeProjectId: null,
  themeId: 'paper',
  backgroundId: 'theme',
  inkId: 'theme',
  chordId: 'theme',
  fontScale: 1,
  stageFontScale: 1.6,
  defaultScrollSpeed: 24,
  keepAwake: true,
};

export async function getSettings(): Promise<Settings> {
  const stored = await get<Settings>(STORE.settings, 'app');
  // Merge, damit neue Felder nach einem Update sinnvolle Werte haben.
  return { ...DEFAULT_SETTINGS, ...stored, id: 'app' };
}

export async function saveSettings(settings: Settings): Promise<void> {
  await put(STORE.settings, settings);
}

/** Merkt sich das gewaehlte Design, ohne dass der Aufrufer alles laden muss. */
export async function setThemeId(themeId: string): Promise<void> {
  const settings = await getSettings();
  await saveSettings({ ...settings, themeId });
}
